import { FC } from 'react';

import { DeliveryChannel } from '@shared/api/giftCoupon/giftCoupon.interface';

import { ApproveModalState, DELIVERY_CHANNEL_SELECT_OPTIONS } from './types';

interface RefundApproveContentProps {
  approveModal: ApproveModalState;
  onChangeRefundAmount: (value: string) => void;
}

const RefundApproveContent: FC<RefundApproveContentProps> = ({
  approveModal,
  onChangeRefundAmount,
}) => (
  <div className="mt-4 space-y-3">
    {(approveModal.recipientName || approveModal.refundPhoneNumber) && (
      <div className="rounded-lg bg-gray-50 px-3 py-2.5">
        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
          신청자
        </p>
        <p className="mt-0.5 text-sm font-semibold text-gray-800">
          {approveModal.recipientName || '-'}
          {approveModal.refundPhoneNumber && (
            <span className="ml-2 text-xs font-normal text-gray-500">
              {approveModal.refundPhoneNumber}
            </span>
          )}
        </p>
      </div>
    )}

    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-3 py-2">
        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
          구매가격 기준 90%
        </p>
      </div>
      <div className="divide-y divide-gray-50 px-3">
        {approveModal.giftItemPrice != null && approveModal.giftItemPrice > 0 && (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-1.5">
              {approveModal.refundBaseSource === 'giftItem' && (
                <span className="rounded bg-orange-100 px-1 py-0.5 text-[12px] font-bold text-orange-600">
                  적용
                </span>
              )}
              <span className="text-xs text-gray-600">주문가격</span>
            </div>
            <div className="text-right">
              <span
                className={`text-sm ${
                  approveModal.refundBaseSource === 'giftItem'
                    ? 'font-bold text-gray-800'
                    : 'text-gray-500'
                }`}
              >
                {approveModal.giftItemPrice.toLocaleString()}원
              </span>
              <span className="ml-1.5 text-[11px] text-gray-500">
                {approveModal.giftItemName}
              </span>
            </div>
          </div>
        )}
      </div>
      {approveModal.refundBasePrice != null && approveModal.refundBasePrice > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-orange-50 px-3 py-2">
          <span className="text-xs font-bold text-orange-700">90% 환불 기준</span>
          <span className="text-sm font-bold text-orange-700">
            {Math.floor(approveModal.refundBasePrice * 0.9).toLocaleString()}원
          </span>
        </div>
      )}
    </div>

    {(approveModal.refundBank || approveModal.refundAccountNumber) && (
      <div className="rounded-lg bg-gray-50 px-3 py-2.5">
        <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
          환불 계좌
        </p>
        <div className="mt-1 space-y-0.5 text-sm text-gray-700">
          <p className="font-semibold">
            {approveModal.refundBank} {approveModal.refundAccountNumber}
          </p>
          {approveModal.refundAccountHolder && (
            <p className="text-xs text-gray-500">
              예금주: {approveModal.refundAccountHolder}
            </p>
          )}
        </div>
      </div>
    )}

    <div>
      <label className="block text-sm font-medium text-gray-700">환불 금액</label>
      <input
        type="number"
        value={approveModal.refundAmount}
        onChange={(e) => onChangeRefundAmount(e.target.value)}
        placeholder="환불 금액 입력"
        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      {approveModal.refundBasePrice != null &&
        approveModal.refundBasePrice > 0 &&
        approveModal.refundAmount &&
        Number(approveModal.refundAmount) !==
          Math.floor(approveModal.refundBasePrice * 0.9) && (
          <p className="mt-1 text-xs text-amber-600">
            90% 기준(
            {Math.floor(approveModal.refundBasePrice * 0.9).toLocaleString()}
            원)과 다른 금액입니다
          </p>
        )}
    </div>
  </div>
);

interface RenewalApproveContentProps {
  approveModal: ApproveModalState;
  onChangeField: (field: keyof ApproveModalState, value: string) => void;
}

const RenewalApproveContent: FC<RenewalApproveContentProps> = ({
  approveModal,
  onChangeField,
}) => (
  <div className="mt-4 space-y-3">
    <div className="flex items-center rounded-lg bg-gray-50 px-3 py-2">
      <span className="text-xs font-medium text-gray-500">상품:</span>
      <span className="ml-2 text-sm font-semibold text-gray-800">
        {approveModal.itemName}
      </span>
      {approveModal.priceRetail && (
        <span className="ml-auto text-xs text-gray-500">
          판매가 {Number(approveModal.priceRetail).toLocaleString()}원
        </span>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700">
        발송 매체 (deliveryChannel)
      </label>
      <select
        value={approveModal.deliveryChannel}
        onChange={(e) => onChangeField('deliveryChannel', e.target.value)}
        className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {DELIVERY_CHANNEL_SELECT_OPTIONS.map((opt) => (
          <option key={opt.value === '' ? '__none__' : opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700">
        판매가 (priceRetail)
      </label>
      <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
        {approveModal.priceRetail
          ? `${Number(approveModal.priceRetail).toLocaleString()}원`
          : '0원'}
        <span className="ml-2 text-xs text-gray-500">(자동)</span>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700">
        도매가 (priceWhole)
      </label>
      <input
        type="number"
        value={approveModal.priceWhole}
        onChange={(e) => onChangeField('priceWhole', e.target.value)}
        placeholder="도매가 입력"
        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700">
        리베이트 (priceRebate)
      </label>
      <input
        type="number"
        value={approveModal.priceRebate}
        onChange={(e) => onChangeField('priceRebate', e.target.value)}
        placeholder="리베이트 입력"
        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
    </div>
  </div>
);

interface ApproveModalProps {
  approveModal: ApproveModalState;
  onClose: () => void;
  onConfirm: () => void;
  onChangeModal: (updated: ApproveModalState) => void;
}

export const ApproveModal: FC<ApproveModalProps> = ({
  approveModal,
  onClose,
  onConfirm,
  onChangeModal,
}) => {
  const handleChangeField = (field: keyof ApproveModalState, value: string) => {
    onChangeModal({
      ...approveModal,
      [field]: field === 'deliveryChannel' ? (value as DeliveryChannel | '') : value,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900">
          {approveModal.type === 'refund' ? '환불 승인' : '연장 승인'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {approveModal.type === 'refund'
            ? '환불 정보를 확인하고 승인 처리하세요.'
            : '가격 정보를 입력한 후 승인 처리하세요.'}
        </p>

        {approveModal.type === 'refund' ? (
          <RefundApproveContent
            approveModal={approveModal}
            onChangeRefundAmount={(value) => handleChangeField('refundAmount', value)}
          />
        ) : (
          <RenewalApproveContent
            approveModal={approveModal}
            onChangeField={handleChangeField}
          />
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
              approveModal.type === 'refund'
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {approveModal.type === 'refund' ? '환불 승인' : '연장 승인'}
          </button>
        </div>
      </div>
    </div>
  );
};
